import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GAME_HAND } from 'constants/gameConstants';
import * as _ from 'lodash';
import {
  calculateMaxTopUp,
  convertToNumber,
  getPlayerStatus,
  hasWinnerDeclarationStarted,
  isFinalRunOutBoard,
  isPastFirstRunOutBoard,
  parseOverBet
} from 'utils/functions';
import type { HandPace } from 'utils/handPace';
import { getTableHistoryUpdates, TableHistoryUpdate } from 'utils/tableHistoryUpdates';

import { runFromSeatAction } from 'components/UserActions/MultiRunActions/voteFromAction';

import { assignIfChanged, mergeArrayItems, mergeIfChanged } from './identityMerge';

export type Player = {
  GUID: string;
  stack: number;
  bet: string;
  lbet: number;
  betDisplay: string;
  action: string;
  status: number;
  openAddFunds: boolean;
  totalBounty: number;
  totalHandBounty: number;
  isMuted: number;
  displayName: string;
  duration: string;
};

export type History = {
  msg: string;
  avatar?: string;
  systemMsg: boolean;
  isShowCard: boolean;
  playerCards?: CardHistory[];
  tableCards?: string[];
  totalPot?: string;
  isShowPot?: boolean;
  isCharge?: boolean;
  /** Client-recorded epoch ms when this entry was first observed locally. */
  ts?: number;
};

export type CardHistory = {
  ID: string;
  bet: string;
  card1: string;
  card2: string;
  avatar: string;
  isFaceDownCard1: boolean;
  isFaceDownCard2: boolean;
};

export type Run = {
  run1: RunWinners;
  run2: RunWinners;
  run3: RunWinners;
  // Full hand pot captured once (before per-run settlement drains it) so every
  // board can show the real pot. Present only while boards are running.
  totalPot?: number;
};

export type RunWinnerDetail = {
  seat: number;
  name: string;
  win: number;
  hand?: string;
};

export type RunWinners = {
  winners: number[];
  winTypes: string[];
  // Durable per-run winner display data (only seats that actually won this run)
  // written by holdem-processor predistpot: name, per-winner share, hand rank.
  potWon?: number;
  details?: RunWinnerDetail[];
};

export type Winner = {
  player: string;
  cards: string;
  hand: string;
  win: number;
  handType: string;
};

export type TablePot = {
  pot: number;
  players: number[];
  winners: Winner[];
  label: string;
};

/** EN-11023: `gameState.pokerActions.straddlePreAction` from cash-game-broadcast. */
export type StraddlePreActionOffer = {
  /** The hand the straddle is for. */
  gameNo: number;
  /** 2x the big blind, as today. */
  amount: number;
  /** Whether this player's choice is stored server-side. */
  set: boolean;
};

export type ChatMessage = {
  GUID: string;
  displayName: string;
  avatar: string;
  messageGUID: string;
  message: string;
  ts: number;
};

// Event identity for duplicate detection. The broadcaster re-derives every row
// from the CURRENT game row on each push (cash-game-broadcast getTableHistory),
// so totalPot (the draining pot), systemMsg/isShowPot/isShowCard (phase-derived)
// and playerCards (hand 5 only) change while the same event repeats - e.g. a
// payout re-sent at hand 15, or the last blind carried from hand 4 into 5.
// Only the text and the board identify the event; a new board is a new street.
const getHistoryEntryKey = (entry: History) =>
  [entry?.msg || '', entry?.isCharge ? '1' : '0', JSON.stringify(entry?.tableCards || [])].join(
    '|'
  );

const isDuplicateHistoryTail = (existing: History[] = [], incoming: History[] = []) => {
  if (incoming.length === 0 || existing.length < incoming.length) return false;

  const existingTail = existing.slice(existing.length - incoming.length);
  return incoming.every(
    (entry, index) => getHistoryEntryKey(entry) === getHistoryEntryKey(existingTail[index])
  );
};

const stampHistory = (history: History[] = [], stampMs: number) =>
  history.map((h: History) => (h && h.ts ? h : { ...h, ts: stampMs }));

// A chat message's identity: its messageGUID, or for legacy entries without
// one, sender + timestamp + text. The chat buffer is deduped by this key, so
// it is unique within it — safe as a React key for the rendered rows.
export const chatMessageKey = (m: ChatMessage): string =>
  m.messageGUID || `${m.GUID || ''}|${m.ts || ''}|${m.message || ''}`;

interface PromoNotification {
  id: number;
  message: string;
}

/**
 * A `gotJoinGame` rejection for THIS table, awaiting acknowledgement. Set by
 * App.tsx with meta.gameID (see services/pendingSeatJoin for how the table is
 * identified) and rendered by SeatJoinErrorModal inside that table's cell —
 * replacing the app-wide ErrorMsgModal that blurred every open table.
 */
export type SeatJoinError = {
  /** Raw engine text; utils/seatJoinErrorCopy turns it into player wording. */
  message: string;
  /** Seat the player asked for, or null if the engine was left to choose. */
  seatId: number | null;
  at: number;
};

const initialState = {
  seatJoinError: null as SeatJoinError | null,
  game: {
    bb: 0,
    bet: 0,
    blindLevels: '',
    card1: '',
    card2: '',
    card3: '',
    card4: '',
    card5: '',
    dealer: '',
    game: '',
    gameID: 0,
    gameNo: '',
    gameType: '',
    hand: '',
    isOmaha: false,
    isOmahaPL: false,
    languageKey: '',
    lastaction: '',
    lastmove: 0,
    lastbet: '',
    move: '',
    mraise: 0,
    msg: '',
    chargeMsg: '',
    overBet: '',
    pot: 0,
    potLimit: 0,
    p4pot: 0,
    p1potwin: 0,
    p2potwin: 0,
    p3potwin: 0,
    p4potwin: 0,
    p5potwin: 0,
    p6potwin: 0,
    p7potwin: 0,
    p8potwin: 0,
    p9potwin: 0,
    p10potwin: 0,
    runs: '',
    // Run-it-twice vote window for THIS table, seconds — resolved server-side
    // (table setting → global MULTIRUNTIME → DEFAULT_VOTE_WINDOW_SECS) and broadcast so the countdown
    // matches the timeout the engine enforces. 0 until the first payload lands.
    multiRunVoteSecs: 0,
    // EN-10985: the between-hand pace the server is running, cash tables only.
    // Display-only, for the QA Performance HUD; absent until the broadcaster has
    // read it (utils/handPace).
    handPace: undefined as HandPace | undefined,
    sb: 0,
    speed: 0,
    stack: '',
    tablelimit: 0,
    tablelow: 0,
    tablelowOverride: 0,
    triggerBuyIn: -1,
    time: 0,
    title: '',
    winner: '',
    winType1: '',
    winType2: '',
    winType3: '',
    winType4: '',
    winType5: '',
    winType6: '',
    winType7: '',
    winType8: '',
    winType9: '',
    p1bet: '',
    p2bet: '',
    p3bet: '',
    p4bet: '',
    p5bet: '',
    p6bet: '',
    p7bet: '',
    p8bet: '',
    p9bet: '',
    p10bet: '',
    p1lbet: 0,
    p2lbet: 0,
    p3lbet: 0,
    p4lbet: 0,
    p5lbet: 0,
    p6lbet: 0,
    p7lbet: 0,
    p8lbet: 0,
    p9lbet: 0,
    p10lbet: 0,
    winners: [] as number[],
    winTypes: [] as string[],
    potWins: [] as number[],
    totalPot: 0,
    lastplayer: '0',
    lastAllInPlayer: '0',
    // EN-663 AC-2.1 — the seat that owns the multi-run offer. A separate fact
    // from lastAllInPlayer above, which every all-in of the hand overwrites.
    firstAllInPlayer: '0',
    highestBetPlayer: {
      GUID: '',
      lbet: 0,
      betDisplay: '',
      stack: 0,
      bet: '',
      action: '',
      status: 0,
      openAddFunds: false
    } as Player,
    tableOverBet: {
      seatId: 0,
      bet: 0
    },
    p1action: '',
    p2action: '',
    p3action: '',
    p4action: '',
    p5action: '',
    p6action: '',
    p7action: '',
    p8action: '',
    p9action: '',
    p10action: '',
    p1status: 0,
    p2status: 0,
    p3status: 0,
    p4status: 0,
    p5status: 0,
    p6status: 0,
    p7status: 0,
    p8status: 0,
    p9status: 0,
    multiRunCards: {},
    runWinners: {
      run1: {
        winners: [],
        winTypes: []
      },
      run2: {
        winners: [],
        winTypes: []
      },
      run3: {
        winners: [],
        winTypes: []
      }
    } as Run,
    pauseStartTime: 0,
    sBustPause: '0',
    pots: [] as TablePot[],
    maximumTopUp: 0,
    deadMoney: 0,
    isPennyTable: false,
    straddle: '',
    hasStraddle: '0|0',
    sbPlayer: '',
    bbPlayer: '',
    tabletype: '',
    startAtTime: 0,
    chatMessages: [] as ChatMessage[],
    lastChatMessage: null as ChatMessage | null,
    tournamentId: '',
    myStatus: '',
    status: '',
    closed: -1,
    seats: 0,
    registeredPlayers: 0,
    ante: 0,
    activePlayers: 0,
    nextSB: 0,
    nextBB: 0,
    nextAnte: 0,
    minPlayers: 0,
    prizePool: 0,
    guarantee: 0,
    currentLevel: 0,
    bust: {
      sBustPause: 0,
      sRebuyFlag: false,
      pauseStartTime: 0,
      elapsedTime: 0,
      duration: 0,
      isBusted: false
    },
    strStartsIn: '',
    schedule: '',
    addOn: 0,
    addOnChips: 0,
    addOnPrice: 0,
    addOnExpireMins: 0,
    canAddOnChips: false,
    rebuyChips: 0,
    rebuyPrice: 0,
    rebuyExpireMins: 0,
    rebuys: 0,
    linkedId: 0,
    isBreak: 0,
    maxSeat: 0,
    nextLevelIsBreak: 0,
    isOnBreak: 0,
    levelStartedAt: 0,
    nextMins: 0,
    currentMins: 0,
    lvlDisplay: 0,
    totalAddOns: 0,
    totalRebuys: 0,
    isMultiFlightBreak: 0,
    currentFlight: 0,
    isMultiFlight: 0,
    nextLevel: '0',
    isFinalMultiFlight: 0,
    entryFee: 0,
    multiBagOption: '',
    isQualified: 0,
    // Tournament re-entry config (from tournaments.* on the gotTexas payload).
    // Declared here so the client's fast re-entry check reads them type-safely
    // instead of via `(game as any)` casts. 0 = late entry not allowed / no window.
    lateEntry: 0,
    lateEntryExpireMins: 0,
    maxSB: 0,
    maxBB: 0,
    maxAnte: 0,
    effectiveRate: 0,
    chargeFrequency: 0,
    minBalanceToPlay: 0,
    addFundsMsg: '',
    lateRegistrationCountDown: 0,
    currentSB: 0,
    currentBB: 0,
    currentAnte: 0,
    bountyAmount: 0,
    moveMsg: '',
    isPostBalancing: 0,
    buyInTriggerTimer: 120, // default value
    callAmount: 0,
    guidWinner: '',
    showdown: {
      currentPot: 0,
      currentStep: 0,
      handType: ''
    },
    seatsWithShowCard: [] as number[],
    seatsWithMuckCard: [] as number[],
    seatsWithVisibleCards: [] as number[],
    preselectedAction: '',
    p1preAction: '',
    p2preAction: '',
    p3preAction: '',
    p4preAction: '',
    p5preAction: '',
    p6preAction: '',
    p7preAction: '',
    p8preAction: '',
    p9preAction: '',
    p1hasAddOnChips: false,
    p2hasAddOnChips: false,
    p3hasAddOnChips: false,
    p4hasAddOnChips: false,
    p5hasAddOnChips: false,
    p6hasAddOnChips: false,
    p7hasAddOnChips: false,
    p8hasAddOnChips: false,
    p9hasAddOnChips: false,
    cardCount: 0,
    closedBetting: 0,
    handRank: '',
    waitlistCount: 0,
    isSatellite: 0,
    privateTable: 0,
    privateTableStartTime: 0,
    isRestrictedToAddedPlayers: 0,
    privatePlayers: [] as string[],
    canJoinPrivateTable: false,
    observerCount: 0
  },
  gameState: {
    startTurn: false,
    preDeal: false,
    noPlayers: 0,
    foldedPlayers: 0,
    notFoldedPlayers: 0,
    showStraddle: false,
    // Seat whose straddle indicator is pinned for the current hand (-1 = none).
    // Latched in updateCurrentTable so the badge survives the whole pre-flop
    // round instead of vanishing when the straddler takes their option.
    straddleIndicatorSeat: -1,
    // Each seat's multi-run vote token (`p{N}action`), latched by seat index for
    // the life of the multi-run sequence. See the latch in updateCurrentTable.
    multiRunVoteActions: [] as string[],
    // `game.gameNo` the latch above was filled under — the hand it belongs to.
    // Identity, not decoration: it is what lets a vote window recognise that the
    // entries it is looking at came from a PREVIOUS hand. See the latch.
    multiRunVoteHand: '',
    // Set on the first frame of a multi-run hand that a winner is declared, and
    // held for the rest of THAT hand (`multiRunVoteHand`): the seats' vote bubble
    // and colour stay dismissed even when a later board's frame looks undeclared.
    // See the latch.
    multiRunVoteDismissed: false,
    allInPlayers: 0,
    nextBBPlayer: 0,
    nextDealer: 0,
    nextStraddlePlayer: 0,
    ingamePlayers: 0,
    playerActiveInHand: 0,
    canBuyTheButton: true,
    gameEnding: false,
    playerActiveInHandNew: 0,
    isShowdown: false,
    startMucking: false,
    canExitTable: false,
    pokerActions: {
      allIn: false,
      bet: false,
      call: false,
      check: false,
      raise: false,
      fold: false,
      isMyTurn: false,
      preCall: false,
      preCallAny: false,
      muckCard: false,
      playerCallAmount: 0,
      showAction: false,
      showCard: false,
      showStraddle: false,
      // EN-11023: "straddle next hand" offer, sent only to the next hand's UTG
      // once they have no decisions left. `set` is whether their choice is stored.
      straddlePreAction: null as StraddlePreActionOffer | null,
      betMultiplier: {
        min: 0,
        x2: 0,
        x3: 0,
        x5: 0,
        oneHalf: 0,
        oneFourth: 0,
        fullPot: 0,
        mraise: 0,
        playerTotalChips: 0,
        playersStack: 0,
        x2isDisabled: false,
        x3isDisabled: false,
        x5isDisabled: false,
        oneHalfIsDisabled: false,
        oneFourthIsDisabled: false,
        fullPotDisabled: false,
        allInDisabled: false,
        isFirstToActPostFlop: false,
        isAllIn: false,
        hasPotLimit: false
      }
    }
  },
  players: [] as Player[],
  ts: 0,
  // Highest server read-time (ms) applied so far. See the version guard in
  // updateCurrentTable — 0 means nothing versioned has been applied yet.
  version: 0,
  // Custom Properties
  currentPlayer: {
    GUID: '',
    playerStatus: 0,
    seatId: -1,
    isMyTurn: false,
    isSeated: false,
    isMultiRunActive: false,
    isWaiting: false,
    isFolded: false,
    isSB: false,
    isBB: false,
    isPostedBlind: false,
    isStraddle: false,
    isPlayerInactive: false,
    isWaitingAndExit: false,
    playerBankAmount: 0,
    playerChargeBalance: 0,
    promoNotifications: [] as PromoNotification[],
    playerGUID: null,
    isJoined: false,
    isRejoining: false,
    clubId: 0,
    chargeCount: 0,
    seatStartTime: null as number | null,
    isMuted: 0,
    duration: '',
    // Mirrors Unity TableModel._joinButtonVisible
    // (Assets/Scripts/Models/TableModel.cs:311). Gates rendering of the
    // main "Join Table" CTA — initial false, set true when the table
    // loads / buy-in opens / stand-up returns the player. Managed via
    // the showJoinButton action.
    joinButtonVisible: false
  },
  tableCharges: [],
  tableHistory: [] as History[],
  tableHistoryInterrupted: false,
  tournamentStatus: []
};

/**
 * Append newly-arrived Table History rows, collapsing ADJACENT identical rows.
 *
 * `tableHistory` is not the incremental feed this reducer and
 * `utils/tablePayloadQueue.ts` both assume it is: the broadcaster re-derives it
 * from the current game row on EVERY push (cash-game-broadcast
 * `getTableHistory` — no cursor, no diff), so the same row arrives again and
 * again while `msg` is unchanged. Two things then multiply it:
 *
 *   - `compactTablePayloads` CONCATENATES the arrays of every payload it
 *     collapses, so a flush holding three snapshots of one msg yields
 *     [X, X, X]; the `msg`-changed gate below passes once and appends all
 *     three — each stamped with the merged payload's single `ts`, which is why
 *     the duplicates share a timestamp.
 *   - run-it-twice winner rows are derived from `runWinners`, NOT from `msg`
 *     (gameTransforms `isMultiRun && handNumber === 14`), so they re-arrive on
 *     every msg change across the settlement window — which the `msg` gate
 *     cannot suppress even in principle.
 *
 * What is collapsed is an ADJACENT REPEAT OF A BLOCK, not just of a single row.
 * A chopped pot emits one row per winner, so a re-derived settlement arrives as
 * [A, B] again and no two neighbouring rows are equal — checking only the last
 * row would let the winner pair through 2-3 times, which is the original symptom
 * for exactly the hands where the money was split.
 *
 * Adjacency is what separates a re-derivation from a genuine repeat: a player
 * checking the flop and then the turn has another event between the two —
 * the opponent acting, or the board being dealt. If that event has no visible
 * rows, the reducer starts a fresh append below. Deduping over a WINDOW of
 * recent rows would swallow that second check and drop a real action.
 *
 * Rows with no `msg` (the board-cards rows) carry no identity to compare on and
 * pass through untouched.
 */
export const appendHistory = (existing: History[], incoming: History[]): History[] => {
  const keyOf = (h: any): string | null =>
    h && typeof h.msg === 'string' && h.msg !== '' ? getHistoryEntryKey(h) : null;
  const out = [...(existing || [])];

  // A re-derivation re-sends the WHOLE settlement as one batch, so the common
  // case is simply "history already ends with exactly what just arrived". That
  // is checked first and has no length bound — which matters now that one board
  // emits a row per POT per winner (AC-7.2), not one row per seat, so a side-pot
  // hand can send far more than the ten rows the scan below was sized for.
  if (
    (incoming || []).length > 0 &&
    out.length >= incoming.length &&
    incoming.every((row, i) => {
      const key = keyOf(row);
      return key !== null && key === keyOf(out[out.length - incoming.length + i]);
    })
  ) {
    return out;
  }

  // Partial overlaps still need the row-by-row scan: a batch that repeats only
  // part of the tail, or arrives split across broadcasts. Bounding the search
  // keeps the cost flat and stops a long stretch of the log ever being mistaken
  // for a repeated block.
  const MAX_BLOCK = 10;

  /** Do the last `size` rows repeat the `size` rows immediately before them? */
  const repeatsPrecedingBlock = (size: number): boolean => {
    if (out.length < size * 2) return false;

    for (let i = 0; i < size; i += 1) {
      const tail = keyOf(out[out.length - size + i]);
      if (tail === null || tail !== keyOf(out[out.length - size * 2 + i])) return false;
    }
    return true;
  };

  (incoming || []).forEach((row) => {
    out.push(row);

    for (let size = 1; size <= MAX_BLOCK; size += 1) {
      if (repeatsPrecedingBlock(size)) {
        out.length -= size;
        break;
      }
    }
  });

  return out;
};

export const currentTableSlice = createSlice({
  name: 'currentTable',
  initialState,
  reducers: {
    setSeatJoinError: (state, action: { payload: SeatJoinError }) => {
      state.seatJoinError = action.payload;
    },
    clearSeatJoinError: (state) => {
      state.seatJoinError = null;
    },
    updateCurrentTable: (state, action) => {
      const { game, gameState, players, tableCharges = [], ts, version } = action.payload;

      // DROP A FRAME THAT IS NOT NEWER THAN THE ONE ALREADY APPLIED.
      //
      // Vote nudges are fire-and-forget and `broadcastTableState` is an async
      // EventBridge target with no reservedConcurrency, so N nudges for one table
      // become N concurrent invocations, each reading at its own moment. Nothing
      // serialises them. Applying every payload unconditionally meant a
      // cold-started invocation that read the PRE-vote row could land after a warm
      // one that read the post-vote row: `runs` and `p{n}action` reverted, so a
      // seat that had voted saw its Run-It buttons RE-ARM and the panel count go
      // backwards, and the stale `msg` re-appended a Table History row —
      // `appendHistory` collapses only adjacent and block repeats, and the newer
      // row now sits between the two copies. That duplicated row is the exact
      // class of defect the nudge was added to fix.
      //
      // `version` is the server's read time in milliseconds (see fetchTableData);
      // `ts` is seconds and cannot separate frames ~200ms apart, which is why it
      // is not used here even though it was already being stored.
      //
      // Frames with no version are applied as before — only the DB-fetch path
      // carries one, and a missing version must never block a payload.
      //
      // WHAT THIS DOES NOT COVER, so nobody reads it as a general ordering
      // guarantee. Only the DB-fetch path stamps `version`; the socket frames
      // dispatched from App.tsx carry none and are the overwhelming majority of
      // what arrives. So an UNVERSIONED frame still applies unconditionally
      // after a versioned one, and a processor/socket frame that was already in
      // flight when the vote landed can still revert `runs` and the seat actions
      // to their pre-vote values — the same defect this watermark fixes for the
      // nudge fan-out, arriving from the other producer.
      //
      // Deliberately not narrowed from here. Rejecting unversioned frames stops
      // the game, and inferring an order for them client-side means silently
      // discarding payloads on a guess — on a live table that is a freeze, which
      // is worse than the stale re-arm it would prevent. Closing it properly
      // means the processor/socket broadcast path carrying the same monotonic
      // token the DB-fetch path already does, at which point this guard covers
      // every producer with no change here beyond deleting this paragraph.
      //
      // Two consequences of the gap are already handled where they land: the
      // vote latch below is keyed on `gameNo`, so a stale frame cannot resurrect
      // a previous hand's bubbles, and MultiRunActions re-derives its own
      // eligibility rather than trusting a single frame.
      const incomingVersion = Number(version);
      if (Number.isFinite(incomingVersion) && incomingVersion > 0) {
        if (incomingVersion <= state.version) return;
        state.version = incomingVersion;
      }

      let previousHistoryGame: TableHistoryUpdate['game'] = state.game;
      for (const update of getTableHistoryUpdates(action.payload)) {
        const incomingTableHistory = update.tableHistory;
        // Scope repeated text to a table and hand number. Processing phase is
        // deliberately excluded: it can advance while the last payout repeats.
        const sameHistoryScope =
          String(previousHistoryGame.gameID ?? '') === String(update.game.gameID ?? '') &&
          String(previousHistoryGame.gameNo ?? '') === String(update.game.gameNo ?? '');
        // A new event can have no visible history rows (for example, dealing
        // the turn). Retain that boundary across both buffer flushes and
        // separate dispatches so the next check is not mistaken for a retry.
        if (
          incomingTableHistory.length === 0 &&
          (!sameHistoryScope || previousHistoryGame.msg !== update.game.msg)
        ) {
          state.tableHistoryInterrupted = true;
        }
        const shouldAppendTableHistory =
          incomingTableHistory.length > 0 &&
          (state.tableHistoryInterrupted ||
            !sameHistoryScope ||
            previousHistoryGame.msg !== update.game.msg ||
            !isDuplicateHistoryTail(state.tableHistory as History[], incomingTableHistory));
        previousHistoryGame = update.game;

        if (!shouldAppendTableHistory) continue;
        // Stamp each newly-arriving item with a timestamp so the Table History
        // panel can show when each event happened. Server payload `ts` is in
        // seconds; fall back to client time if it's missing.
        const stampMs = (() => {
          const tsNum = Number(update.ts);
          if (!Number.isFinite(tsNum) || tsNum <= 0) return Date.now();
          return tsNum < 1e12 ? tsNum * 1000 : tsNum;
        })();
        const stampedNew = stampHistory(incomingTableHistory, stampMs);
        const historyItems =
          sameHistoryScope && !state.tableHistoryInterrupted
            ? appendHistory(state.tableHistory as History[], stampedNew)
            : [...state.tableHistory, ...appendHistory([], stampedNew)];
        state.tableHistoryInterrupted = false;
        const historyItemsLen = historyItems.length;
        const historyStartIndex = Math.max(0, historyItemsLen - 100);
        state.tableHistory = historyItems.slice(historyStartIndex, historyItemsLen) as History[];
      }

      // Merge chat messages instead of letting the spread below overwrite them.
      // Each server push may carry only a partial chatMessages slice (just the
      // newest messages, or just messages since the last update). Wholesale
      // replacement wiped previously-received messages — so on a subsequent
      // open of the chat panel the user saw an empty / shrunken history.
      // Dedupe by messageGUID, fall back to a synthetic key when missing.
      const incomingChat: ChatMessage[] = Array.isArray(game.chatMessages) ? game.chatMessages : [];
      const existingChat: ChatMessage[] = (state.game.chatMessages as ChatMessage[]) || [];
      const mergedChat: ChatMessage[] = (() => {
        if (incomingChat.length === 0) return existingChat;
        const seen = new Set<string>();
        const out: ChatMessage[] = [];
        // Existing first so older messages keep their position when timestamps tie.
        for (const m of [...existingChat, ...incomingChat]) {
          if (!m) continue;
          const key = chatMessageKey(m);
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(m);
        }
        // Keep chronological order — server may send batches out of order.
        out.sort((a, b) => (Number(a.ts) || 0) - (Number(b.ts) || 0));
        // Cap the buffer so memory doesn't grow unboundedly over a long session.
        return out.length > 200 ? out.slice(-200) : out;
      })();

      // PERF: identity-preserving merges (see reducers/identityMerge.ts).
      // Wholesale `state.game = { ...state.game, ...game }` gave every
      // subscriber a new identity on every server push and re-rendered the
      // whole table per message (~55ms of blocked main thread). Only fields
      // whose VALUES changed are written, so untouched slices keep their
      // identity and their subscribers stay asleep.
      // chatMessages is merged separately above; winners/winTypes/potWins/
      // tableOverBet are DERIVED below - merging the payload's stale copies
      // first would double-write them and churn identity on every message.
      const {
        chatMessages: _incomingChatIgnored,
        winners: _derivedWinners,
        winTypes: _derivedWinTypes,
        potWins: _derivedPotWins,
        tableOverBet: _derivedOverBet,
        lastChatMessage: _derivedLastChat,
        isOmaha: _derivedIsOmaha,
        ...gameRest
      } = game;
      mergeIfChanged(state.game, gameRest);
      assignIfChanged(state.game, 'chatMessages', mergedChat);
      state.game.isOmaha = state.game.game === 'omaha';
      mergeIfChanged(state.gameState, gameState);
      mergeArrayItems(state, 'players', players);

      state.ts = ts;

      // Straddle indicator latch. The per-seat action pill flips off 'straddle'
      // the moment the straddler takes their option (e.g. a pre-flop raise), so
      // the indicator can't be driven by the live action. Instead, latch the
      // straddle seat (hasStraddle = "1|<seatId>") and keep it pinned for the
      // ENTIRE pre-flop round, clearing only once the flop is dealt, at
      // showdown, or when the hand ends before the flop (hand >= DEAL_FLOP).
      const handNum = parseInt(state.game.hand, 10);
      const isPreFlop = handNum >= GAME_HAND.ADJUST_BLINDS && handNum < GAME_HAND.DEAL_FLOP;
      if (!isPreFlop) {
        state.gameState.straddleIndicatorSeat = -1;
      } else {
        const [straddleFlag, straddleSeat] = String(state.game.hasStraddle || '0|0').split('|');
        if (straddleFlag === '1' && +straddleSeat >= 0) {
          state.gameState.straddleIndicatorSeat = +straddleSeat;
        }
        // Otherwise keep the seat already latched for the rest of pre-flop.
      }

      // EN-663 AC-5.3: the multi-run vote bubbles have to outlive the street
      // that wipes them. The engine blanks every `p{N}action` each time it deals
      // the next street (and again when it finishes the hand), so a vote cast in
      // the all-in window survived a single hand-step: the bubbles flashed and
      // were gone before the first extra board landed, let alone the payout.
      //
      // So the vote is latched here instead of read live, and held through the
      // whole RUN-OUT — EVERY board the vote bought, not just the first — then
      // dropped once the LAST board has been dealt and is being paid — unless
      // the dismissal below has already taken it off the felt.
      //
      // `runs` is "<totalRuns>,<stage>,<currentRun>", so the last board is the
      // one where `currentRun` has caught up with `totalRuns`; payouts run at
      // hand 14. This used to clear at the FIRST payout (`currentRun > 1`),
      // which took the bubbles off the felt after board 1 of a 2x/3x run-out.
      //
      // The `!multiRunInFlight` arm still carries the hands that never reach a
      // payout (window closed on a fold, fork abandoned): `runs` stops naming a
      // run ('0'/'-1'/'-2') as the next hand starts, and that clears the latch.
      //
      // EN-10899: and the vote is DISMISSED — bubble and colour, every seat — on
      // the first frame a winner is declared, for the rest of the hand. Read per
      // frame, that signal re-opens on later frames: RunMultiple resets
      // `showdown` and the winner columns as it swaps each next board in, so a
      // board's first frames carry no winners, and an unversioned frame read
      // before the declaration can still land after it (see the watermark note
      // above). Hence a flag, set once and cleared only when the hand changes.
      // `isPastFirstRunOutBoard` sets it too, for a client whose compacted
      // backlog skipped board 1's declaration frames altogether.
      const multiRunInFlight =
        isMultiRunActive(state.game) || String(state.game.runs ?? '').includes('v');
      const paying = isFinalRunOutBoard(state.game) && handNum >= GAME_HAND.PAY_WINNERS;
      if (!multiRunInFlight || paying) {
        if (state.gameState.multiRunVoteActions.length > 0) {
          state.gameState.multiRunVoteActions = [];
        }
        // The final payout keeps the hand dismissed; only leaving the run-out
        // (the hand is over) releases it.
        if (!multiRunInFlight && state.gameState.multiRunVoteDismissed) {
          state.gameState.multiRunVoteDismissed = false;
        }
      } else {
        // Neither arm above fires on a `v` frame, so without this the previous
        // hand's bubbles carry into the next voting window whenever the client
        // never sees the frames that clear them. It does not have to drop a
        // subscription to miss them: a backgrounded table buffers its payloads
        // and App.tsx `compactTablePayloads` folds the whole backlog into ONE
        // dispatch, so a table that goes away mid-run-out and comes back during
        // the next all-in jumps straight from a run-out frame to a `v` frame.
        // `gameNo` is the hand counter, so a different one means the latched
        // choices are answers to a question nobody is being asked any more.
        const voteHand = String((state.game as any).gameNo ?? '');
        if (state.gameState.multiRunVoteHand !== voteHand) {
          state.gameState.multiRunVoteHand = voteHand;
          if (state.gameState.multiRunVoteActions.length > 0) {
            state.gameState.multiRunVoteActions = [];
          }
          state.gameState.multiRunVoteDismissed = false;
        }
        if (
          !state.gameState.multiRunVoteDismissed &&
          (hasWinnerDeclarationStarted(state.game) || isPastFirstRunOutBoard(state.game))
        ) {
          state.gameState.multiRunVoteDismissed = true;
        }
        if (state.gameState.multiRunVoteDismissed) {
          if (state.gameState.multiRunVoteActions.length > 0) {
            state.gameState.multiRunVoteActions = [];
          }
        } else {
          for (let seat = 1; seat <= 10; seat += 1) {
            const token = (state.game as any)[`p${seat}action`];
            // Compared before writing, like the merges above: an unguarded write would
            // give the array a new identity on every frame of the sequence.
            if (runFromSeatAction(token) && state.gameState.multiRunVoteActions[seat] !== token) {
              state.gameState.multiRunVoteActions[seat] = token;
            }
          }
        }
      }

      assignIfChanged(state.game, 'winners', parseWinnerToArray(state.game.winner));
      assignIfChanged(state.game, 'winTypes', mapWinTypeToArray(state.game));
      assignIfChanged(state.game, 'potWins', mapPlayerPotWinToArray(state.game));
      assignIfChanged(state.game, 'tableOverBet', parseOverBet(state.game.overBet));

      state.currentPlayer.isMultiRunActive = isMultiRunActive(state.game);

      const seatId = recomputeSeatDerivedFields(state);

      if (game.chatMessages.length > 0 && seatId > 0) {
        assignIfChanged(
          state.game,
          'lastChatMessage',
          game.chatMessages?.filter((f: ChatMessage) => !!f).slice(-1)[0]
        );
      } else {
        state.game.lastChatMessage = null;
      }

      mergeIfChanged(state.tableCharges, tableCharges);
      // state.currentPlayer.chargeCount = (state.tableCharges as any)[
      //   `p${state.currentPlayer.seatId}name`
      // ].multiplier;
    },

    // Identity (playerGUID) resolves separately from — and often later than —
    // this table's game/players data: a guest's public view of a table can
    // load before the post-login handshake tells us who "we" are. Without
    // this recompute, isSeated/seatId stayed stuck at their guest-view values
    // (playerGUID never matched any seat) until the NEXT unrelated
    // updateCurrentTable happened to arrive — a real gap where an
    // already-seated player briefly reads as not seated, wrongly letting a
    // post-login auto-join fire again for a table they never left.
    updateCurrentPlayerGUID: (state, action) => {
      state.currentPlayer.playerGUID = action.payload;
      recomputeSeatDerivedFields(state);
    },

    addHistory: (state, action) => {
      const history = action.payload as History[];
      const nowMs = Date.now();
      const stamped = stampHistory(history, nowMs);

      state.tableHistory = state.tableHistory
        ? ([...state.tableHistory, ...stamped] as History[])
        : ([...stamped] as History[]);

      state.game.chargeMsg = history[0]?.msg;
    },

    updateCurrentPlayerBankAmount: (state, action) => {
      // Ensure playerBankAmount is stored as a number, removing any formatting
      const rawAmount = action.payload?.response?.playWinpot;
      if (rawAmount !== undefined && rawAmount !== null) {
        if (typeof rawAmount === 'string') {
          state.currentPlayer.playerBankAmount = parseFloat(rawAmount.replace(/,/g, '')) || 0;
        } else {
          state.currentPlayer.playerBankAmount = parseFloat(rawAmount) || 0;
        }
      }

      // Handle charge balance similarly
      const rawCharge = action.payload?.response?.playChargeBalance;
      if (rawCharge !== undefined && rawCharge !== null) {
        if (typeof rawCharge === 'string') {
          state.currentPlayer.playerChargeBalance = parseFloat(rawCharge.replace(/,/g, '')) || 0;
        } else {
          state.currentPlayer.playerChargeBalance = parseFloat(rawCharge) || 0;
        }
      }

      // console.log(action.payload?.response);
      if (state.currentPlayer.seatId > 0) {
        let ppot = (state.game as any)[`p${state.currentPlayer.seatId}pot`];
        if (ppot !== undefined && ppot !== null) {
          ppot = state.game.isPennyTable ? convertToNumber(ppot) : parseInt(ppot);

          state.game.maximumTopUp = calculateMaxTopUp(
            state.game.tablelimit,
            ppot,
            state.currentPlayer.playerBankAmount
          );
        }
      }
    },

    updateTournamentStatus: (state, action) => {
      state.tournamentStatus = action.payload;
    },

    // Merge a single tournament status object into the existing array (for API Gateway updates)
    mergeTournamentStatus: (state, action) => {
      const incoming = action.payload;
      const arr: any[] = Array.isArray(state.tournamentStatus) ? [...state.tournamentStatus] : [];
      const idx = arr.findIndex(
        (s: any) => s.tournamentId === incoming.tournamentId && s.playerId === incoming.playerId
      );
      if (idx >= 0) {
        arr[idx] = incoming;
      } else {
        arr.push(incoming);
      }
      state.tournamentStatus = arr as any;
    },

    // Evict every cached gotTnmStatus row for one tournament. Used when opening
    // the lobby Tournament Info so a RESTARTED tournament (same tournamentId,
    // fresh instance) can't show rows left over from the finished prior instance
    // — the re-subscribe then repopulates from the live feed. Falsy id is a no-op.
    clearTournamentStatusFor: (state, action) => {
      const tid = action.payload;
      if (tid === undefined || tid === null || tid === '') return;
      if (!Array.isArray(state.tournamentStatus)) return;
      state.tournamentStatus = state.tournamentStatus.filter(
        (s: any) => Number(s?.tournamentId) !== Number(tid)
      ) as any;
    },

    updateCurrentPlayerHasAddOnChips: (state, action) => {
      const seatId = action.payload;
      const game = { ...state.game, [`p${seatId}hasAddOnChips`]: true };

      state.game = game;
    },

    // Mirrors Unity TableController.ShowJoinButton(bool) at
    // Assets/Scripts/Controllers/TableController.cs:297. Called at:
    //   - TablesView.cs:2339 — table loads
    //   - BuyInView.cs:121   — buy-in view opens
    //   - BuyInView.cs:647   — false while waiting for seat availability
    //   - BuyInView.cs:649   — true once WaitForSeatAvailability completes
    showJoinButton: (state, action) => {
      state.currentPlayer.joinButtonVisible = Boolean(action.payload);
    },

    // Empty every seat on the table. Used when a table is flagged CLOSED (its
    // gameID is in gotTnmStatus.closedGameIDs): the engine has already
    // transferred every player elsewhere, but a closed table stops sending
    // gotTexas so the felt would otherwise keep showing the stale occupants.
    // `players: []` is the same "no one seated" shape as the initial state, so
    // every seat renders as Open. The POV player's own currentPlayer state is
    // left intact so the in-table "table closed" prompt (which keys off
    // isSeated) still shows.
    clearTablePlayers: (state) => {
      state.players = [];
    }
  },
  extraReducers: (builder) => {
    // useTableSelector can fall back to this single-table mirror after the
    // per-table slice is removed. An explicit exit must clear both sources.
    builder.addCase(
      'tables/removeTable',
      (state, action: PayloadAction<string, 'tables/removeTable'>) => {
        if (String(state.game.gameID) !== String(action.payload)) return;
        return {
          ...initialState,
          currentPlayer: {
            ...initialState.currentPlayer,
            playerGUID: state.currentPlayer.playerGUID,
            playerBankAmount: state.currentPlayer.playerBankAmount
          }
        };
      }
    );
  }
});

export const {
  updateCurrentTable,
  updateCurrentPlayerGUID,
  addHistory,
  updateCurrentPlayerBankAmount,
  updateTournamentStatus,
  mergeTournamentStatus,
  clearTournamentStatusFor,
  updateCurrentPlayerHasAddOnChips,
  showJoinButton,
  clearTablePlayers,
  setSeatJoinError,
  clearSeatJoinError
} = currentTableSlice.actions;

export default currentTableSlice.reducer;

// Derives everything about "am I in this table, and where" from state.game /
// state.players + the currently-known playerGUID. Shared by updateCurrentTable
// (a fresh game payload) and updateCurrentPlayerGUID (identity resolving on
// its own, against game/players data that may already be loaded) so identity
// resolving late doesn't leave isSeated stuck at a stale reading until the
// next unrelated game update happens to arrive.
const recomputeSeatDerivedFields = (state: any): number => {
  const seatId = _.findIndex(
    state.players,
    (p: any) => !!p?.GUID && p?.GUID === state.currentPlayer.playerGUID
  );

  state.currentPlayer.seatId = seatId;
  state.currentPlayer.isMyTurn = seatId === +state.game.move;
  state.currentPlayer.isSeated = seatId > 0;

  if (state.currentPlayer.isSeated && !state.currentPlayer.seatStartTime) {
    state.currentPlayer.seatStartTime = Date.now();
  }

  if (!state.currentPlayer.isSeated) {
    state.currentPlayer.seatStartTime = null;
  }

  const { isWaiting } = getPlayerStatus(state.game, seatId);

  const playerBetAction = (state.game as any)[`p${seatId}bet`]?.substring(0, 1);
  const playerAction = (state.game as any)[`p${seatId}action`];
  const playerStatus = (state.game as any)[`p${seatId}status`];
  state.currentPlayer.isWaiting = isWaiting;
  state.currentPlayer.isFolded = playerBetAction === 'F';
  state.currentPlayer.isSB = seatId === +state.game.sbPlayer;
  state.currentPlayer.isBB = seatId === +state.game.bbPlayer;
  state.currentPlayer.isPostedBlind = playerAction === 'ANTE';
  state.currentPlayer.isStraddle = playerAction === 'straddle';
  state.currentPlayer.isPlayerInactive = ['J', 'F'].includes(playerBetAction);
  state.currentPlayer.isJoined = playerBetAction === 'J';
  state.currentPlayer.isWaitingAndExit = [17, 18].includes(+playerStatus);
  state.currentPlayer.isRejoining = +playerStatus === 10;
  state.currentPlayer.playerStatus = +playerStatus;
  state.currentPlayer.isMuted = (state.currentPlayer as any)[`p${seatId}status`];
  state.currentPlayer.duration = (state.currentPlayer as any)[`p${seatId}status`];

  let ppot = (state.game as any)[`p${seatId}pot`];
  ppot = state.game.isPennyTable ? convertToNumber(ppot) : parseInt(ppot);

  state.game.maximumTopUp = calculateMaxTopUp(
    state.game.tablelimit,
    ppot,
    state.currentPlayer.playerBankAmount
  );

  return seatId;
};

const parseWinnerToArray = (winner: string) => {
  /*
   * Convert empty strings to -1 whenever we start introducing
   * vacant seats between players
   *
   * winner.map((string) => (string ? parseInt(string) : -1))
   *
   * Example:
   *   winners  = [2, 1, 4, -1, 5, -1, 3]
   *   -1 are empty seats
   */

  // Empty / missing winner column → EMPTY array, not `[0]`.
  //
  // `""` split by `,` yields `[""]`, and `+"" === 0`, so the naive version
  // returned `[0]` — a phantom "seat 0 is a winner" that broke every
  // consumer trying to detect "is there a winner yet?" via `winners.length`.
  //
  // The specific bug this caused (EN-10561 second pass — confetti didn't
  // fire on real showdown, only on everyone-fold): showdown is a two-write
  // sequence on the engine — advance `hand` to 13, THEN in the case-13
  // tick call updateWinners to set the `winner` column. In between, the
  // client polls `{hand: '13', winner: ''}` and the confetti effect's
  // `resolveWinCelebration` guard saw `winnerCount = 1` (length of `[0]`)
  // AND `hand >= 13`, so it BURNED its `celebratedRef` edge with an empty
  // fire. The next poll — where the real winner had landed — hit
  // `alreadyFired` and returned `fire: false`. Everyone-fold bypassed the
  // race because `updateTableAllFoldSetWinner` sets `hand='14'` AND
  // `winner=<seat>` in one atomic SQL UPDATE, so the client never
  // observed the transient empty state.
  //
  // Returning `[]` here keeps `winners.length` honest — it's zero when
  // there is no winner. Every current consumer of `state.game.winners`
  // (Game.tsx confetti + Pot.tsx's initial useState seed) tolerates or
  // benefits from that.
  if (!winner) return [];
  return winner.split(',').map((m) => +m);
};

const mapWinTypeToArray = (game: any) => {
  /*
   * Get winner types based on the winners[] array
   * Example:
   *   winners  = [  2,    1,    3,    4,    5]
   *   winTypes = [win, side, side, side, side]
   *
   *   player 2 is win, player 1 is side, player 3 is side, so on...
   */

  return [
    game.winType1,
    game.winType2,
    game.winType3,
    game.winType4,
    game.winType5,
    game.winType6,
    game.winType7,
    game.winType8,
    game.winType9
  ];
};

const mapPlayerPotWinToArray = (game: any) => {
  return [
    0,
    +game.p1potwin,
    +game.p2potwin,
    +game.p3potwin,
    +game.p4potwin,
    +game.p5potwin,
    +game.p6potwin,
    +game.p7potwin,
    +game.p8potwin,
    +game.p9potwin,
    +game.p10potwin
  ];
};

const isMultiRunActive = (game: any) => {
  return (game.runs?.split(',').length ?? 0) === 3;
};
